import { animated, useSpring } from '@react-spring/web';
import {
  createUseGesture,
  dragAction,
  GestureKey,
  Handler,
  moveAction,
  pinchAction,
  wheelAction,
} from '@use-gesture/react';
import throttle from 'lodash.throttle';
import React, { Dispatch, MouseEventHandler, ReactNode, SetStateAction, useEffect, useState } from 'react';

import { isInt } from '../../utils/it-int';
import { useMindmap } from './use-mindmap';

const useGesture = createUseGesture([dragAction, pinchAction, wheelAction, moveAction]);

type InteractableMapProps = {
  children: ReactNode;
  initialPosition: {
    x: number;
    y: number;
  };
  onMapClick?: MouseEventHandler<HTMLDivElement>;
  onMove: (x: number, y: number) => void;
  onDrawingSelection: (
    topLeftCords: { x: number; y: number },
    bottomRightCords: { x: number; y: number },
    append: boolean,
  ) => void;
  isEditingNode: boolean;
  isRootNodeDragging: boolean;
  setIsRootNodeDragging: Dispatch<SetStateAction<boolean>>;
  isDrawingSelection: boolean;
  setIsDrawingSelection: React.Dispatch<React.SetStateAction<boolean>>;
};

export const InteractableMap = ({
  children,
  initialPosition,
  onMapClick,
  onMove,
  onDrawingSelection,
  isEditingNode,
  isRootNodeDragging,
  setIsRootNodeDragging,
  isDrawingSelection,
  setIsDrawingSelection,
}: InteractableMapProps) => {
  const {
    interactableMap: {
      setMousePosition,
      scale,
      interactableMapSpring,
      mindmapHeight,
      mindmapWidth,
      pageClientCoordsToMapCoords,
      updateStyle,
    },
  } = useMindmap();
  const [lastMouseDownLocation, setLastMouseDownLocation] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [initialPositioned, setInitialPositioned] = useState<boolean>(false);
  const containerRef = React.useRef(null);
  const containerContainerRef = React.useRef(null);
  const mapRef = React.useRef(null);

  const [willChange, setWillChange] = useState<'auto' | 'transform'>('auto');
  // will-change: "transform" will fix performance issues during animations on mobile,
  // but won't rerasterize it's children (on desktop) when the transform changes.
  // Therefore, use "transform" during animations and "auto" otherwise to
  // trigger a re-rasterization when the transformation finished.
  // (see https://developer.chrome.com/blog/re-rastering-composite/#recommended-actions)
  const onAnimationStart: Handler<GestureKey> = ({ distance }) => {
    const isScrolling = isInt(distance[1]);
    // mousewheel scrolling works with predefined steps and therefore doesn't need/has an animation
    if (!isScrolling) setWillChange('transform');
  };
  useGesture(
    {
      onPinchStart: onAnimationStart,
      onWheelStart: onAnimationStart,
      onDragStart: () => setWillChange('transform'),
      onPinchEnd: () => setWillChange('auto'),
      onWheelEnd: () => setWillChange('auto'),
      onDragEnd: () => setWillChange('auto'),
    },
    { target: containerContainerRef },
  );

  useGesture(
    {
      onMove: (e) => {
        const mapCoords = pageClientCoordsToMapCoords(e.event.clientX, e.event.clientY);
        if (mapCoords) {
          onMove(mapCoords.x, mapCoords.y);
          setMousePosition(e.event.clientX, e.event.clientY);
        }
      },
      onDrag: ({ pinching, cancel, offset: [x, y], down, buttons, event, initial: [ix, iy], ctrlKey, type }) => {
        if (pinching) return cancel();

        if (shouldDrag(event as PointerEvent, buttons, type)) {
          updateStyle({ x, y, immediate: down });
        } else if (buttons != 0 && type === 'pointermove' && !style.isNodeDragging.get() && !isEditingNode) {
          event = event as PointerEvent;
          const isCursorLeftFromInitialX = event.clientX - ix <= 0;
          const isCursorAboveInitialY = event.clientY - iy <= 0;
          drawSelectionSpring[1].start({
            x: isCursorLeftFromInitialX ? event.clientX : ix,
            y: isCursorAboveInitialY ? event.clientY : iy,
            width: isCursorLeftFromInitialX ? ix - event.clientX : event.clientX - ix,
            height: isCursorAboveInitialY ? iy - event.clientY : event.clientY - iy,
            immediate: down,
            isCursorLeftFromInitialX,
            isCursorAboveInitialY,
          });
          selectInSelectionDrawing(ctrlKey);
          if (!isDrawingSelection) setIsDrawingSelection(true);
        }
      },
      onDragStart: ({ event, buttons, type }) => {
        if (shouldDrag(event as PointerEvent, buttons, type)) {
          api.start({ cursor: 'grabbing', isDraggingMap: true });
        }
      },
      onDragEnd: () => {
        drawSelectionSpring[1].start({
          x: 0,
          y: 0,
          width: 0,
          height: 0,
          immediate: true,
        });
        api.start({ cursor: 'auto', isDraggingMap: false });
        if (isRootNodeDragging) setIsRootNodeDragging(false);
        if (isDrawingSelection) setIsDrawingSelection(false);
      },
      onPinch: ({ origin: [ox, oy], offset: [s], direction: [d], down, type, event }) => {
        // @ts-ignore
        const isMouseScroll = isInt(event.deltaY);
        const isTouchscreen = type == 'pointermove' || type == 'pointerdown' || type == 'pointerup';
        const isTouchpad = !isTouchscreen && !isMouseScroll;

        if (isTouchscreen || !isMouseScroll) {
          let newScale: number;
          if (isTouchpad) {
            // pinch with touchpad: calculate newScale with event.deltaY
            // @ts-ignore
            let scaleFactor = event.deltaY / 100;
            const scaleBy = d > 0 ? 1 - scaleFactor : 1 + scaleFactor;
            scale({ toPageCoords: { x: ox, y: oy }, enlarge: d > 0, scaleBy });
          } else {
            // pinch with touchscreen: use scale s from onPinch directly
            newScale = s;
            scale({ toPageCoords: { x: ox, y: oy }, enlarge: d > 0, newScale });
          }
        }
      },
      onWheel: ({ event, ctrlKey, wheeling, down, shiftKey, delta }) => {
        event.preventDefault();

        // @ts-ignore
        const isPinching = !isInt(event.deltaY);

        if (ctrlKey && !isPinching) {
          // scale on ctrl+mousewheel_scrolling
          if (wheeling) {
            scale({ toPageCoords: { x: event.clientX, y: event.clientY }, enlarge: event.deltaY > 0 });
          }
        } else if (shiftKey) {
          // move on x-axis on shift+mousewheel_scrolling
          let newX = style.x.get() + -delta[1];
          api.start({ x: newX, immediate: true });
        } else {
          // two-finger xy-movement on touchpad
          let newX = style.x.get() + -delta[0];
          let newY = style.y.get() + -delta[1];
          api.start({ x: newX, y: newY, immediate: true });
        }
      },
    },
    {
      target: containerRef,
      drag: { from: () => [style.x.get(), style.y.get()], pointer: { buttons: [1, 2] } },
      pinch: { eventOptions: { passive: false } },
      wheel: { eventOptions: { passive: false } },
    },
  );

  // only allow drag on mobile or desktop right-click
  const shouldDrag = (event: PointerEvent, buttons: number, type: string) => {
    const isMouse = event.pointerType == 'mouse';
    const isDesktopDrag = isMouse && buttons == 2;
    // @ts-ignore KeyboardEvent don't drag when editing a text and pressing arrows
    const isArrow = event?.key?.startsWith('Arrow');
    let isDraggingRootNode = isRootNodeDragging;
    if (!isEditingNode && isMouse && !isDraggingRootNode && !style.isNodeDragging.get() && !style.isDraggingMap.get()) {
      // @ts-ignore
      event?.path?.forEach((target) => {
        let isRootDatasetValue = target?.dataset?.isRoot;
        if (isRootDatasetValue) {
          isDraggingRootNode = isRootDatasetValue == 'true' && type === 'pointermove';
        }
      });
      if (isDraggingRootNode && !isRootNodeDragging) setIsRootNodeDragging(true);
    }

    return ((isDesktopDrag || !isMouse) && !(isEditingNode && isArrow)) || (!isEditingNode && isDraggingRootNode);
  };

  const drawSelectionSpring = useSpring(() => ({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    isCursorLeftFromInitialX: false,
    isCursorAboveInitialY: false,
    config: { tension: 0, friction: 0, mass: 0 },
  }));

  const selectInSelectionDrawing = throttle((ctrlKey: boolean) => {
    const drawSelectionStyle = drawSelectionSpring[0];

    const selectionMapCoordsTopLeft = pageClientCoordsToMapCoords(
      drawSelectionStyle.x.get(),
      drawSelectionStyle.y.get(),
    );
    if (selectionMapCoordsTopLeft) {
      const selectionMapWidth = drawSelectionStyle.width.get() / style.scale.get();
      const selectionMapHeight = drawSelectionStyle.height.get() / style.scale.get();
      const selectionMapCordsBottomRight = {
        x: selectionMapCoordsTopLeft.x + selectionMapWidth,
        y: selectionMapCoordsTopLeft.y + selectionMapHeight,
      };

      onDrawingSelection(selectionMapCoordsTopLeft, selectionMapCordsBottomRight, ctrlKey);
    }
  }, 50);

  useEffect(() => {
    if (!initialPositioned && initialPosition) {
      setInitialPositioned(true);
      api.start(initialPosition);
    }
  }, [initialPosition]);

  // use-gesture recommendations
  useEffect(() => {
    const handler = (e: Event) => e.preventDefault();
    document.addEventListener('gesturestart', handler);
    document.addEventListener('gesturechange', handler);
    document.addEventListener('gestureend', handler);
    return () => {
      document.removeEventListener('gesturestart', handler);
      document.removeEventListener('gesturechange', handler);
      document.removeEventListener('gestureend', handler);
    };
  }, []);

  if (!interactableMapSpring || !mindmapHeight || !mindmapWidth) return <span>Loading...</span>;

  const [style, api] = interactableMapSpring;

  return (
    <>
      <animated.div
        style={{
          ...drawSelectionSpring[0],
          backgroundColor: '#BFDBFE',
          border: '1px solid #3B82F6',
          position: 'fixed',
          zIndex: 2000,
          opacity: 0.6,
        }}
      ></animated.div>
      <div
        ref={containerContainerRef}
        style={{ touchAction: 'none' }}
        onMouseDown={(e) => setLastMouseDownLocation({ x: e.pageX, y: e.pageY })}
        onClick={(e) => {
          if (
            lastMouseDownLocation.y - 5 < e.pageY &&
            lastMouseDownLocation.y + 5 > e.pageY &&
            lastMouseDownLocation.x - 5 < e.pageX &&
            lastMouseDownLocation.x + 5 > e.pageX
          ) {
            // there was almost no movement between mouse down and click => handle it as a click on the map
            if (onMapClick) onMapClick(e);
          }
        }}
      >
        <div style={{ height: '100vh', touchAction: 'none' }} ref={containerRef}>
          <animated.div ref={mapRef} style={{ ...style, width: mindmapWidth, height: mindmapHeight, willChange }}>
            {children}
          </animated.div>
        </div>
      </div>
    </>
  );
};
